import React, { useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';

const DistributorLedgerTable = ({
  data = [],
  page = 1,
  limit = 20,
  loading = false,
  onSortChange,
  orderBy = 'id',
  orderDir = 'DESC',
  columnFilters = {},
  onColumnFilterChange,
  onApplyFilters,
}) => {
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  // Double scroll sync
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };
    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [data]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleSort = (columnKey) => {
    if (onSortChange) {
      const nextDir = orderBy === columnKey && orderDir === 'DESC' ? 'ASC' : 'DESC';
      onSortChange(columnKey, nextDir);
    }
  };

  const getSortIcon = (columnKey) => {
    if (orderBy !== columnKey) return faSort;
    return orderDir === 'ASC' ? faSortUp : faSortDown;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onApplyFilters();
    }
  };

  return (
    <div className="panel panel-default with-rounded-corner with-shadow" style={{ marginBottom: 0 }}>
      {/* Double Scroll Container */}
      <div className="large-table-container-3">
        {/* Top Scrollbar */}
        <div
          ref={topScrollbarRef}
          onScroll={handleTopScroll}
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            height: '14px',
            marginBottom: '1px',
            background: '#f8f9fa',
          }}
        >
          <div ref={topScrollbarContentRef} style={{ height: '1px' }}></div>
        </div>

        {/* Table Container */}
        <div
          ref={tableContainerRef}
          onScroll={syncScroll}
          style={{
            overflowY: 'auto',
            height: 'calc(112vh - 250px)',
            overflowX: 'auto',
            position: 'relative',
          }}
        >
          <table className="table table-condensed table-hover bg-white sticky-table" style={{ fontSize: '13px', marginBottom: 0 }}>
            <thead>
              {/* Row 1: Sortable Columns */}
              <tr style={{ cursor: 'pointer', background: '#f4f6f8' }}>
                <th style={{ width: '45px' }}>#</th>
                <th>Order Ref</th>
                <th onClick={() => handleSort('created_on')}>
                  Date & Time <FontAwesomeIcon icon={getSortIcon('created_on')} />
                </th>
                <th>Distributor</th>
                <th>Subscriber / Client</th>
                <th>Subscribed Plan</th>
                <th onClick={() => handleSort('subscription_amount')}>
                  Plan Amount (₹) <FontAwesomeIcon icon={getSortIcon('subscription_amount')} />
                </th>
                <th onClick={() => handleSort('commission_amount')}>
                  Commission (₹) <FontAwesomeIcon icon={getSortIcon('commission_amount')} />
                </th>
              </tr>

              {/* Row 2: Inline Column Filters */}
              <tr>
                <th></th>
                <th>
                  <input
                    type="text"
                    placeholder="Order ref..."
                    value={columnFilters.orderId || ''}
                    onChange={(e) => onColumnFilterChange('orderId', e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ width: '90px', padding: '3px 6px', fontSize: '11.5px', borderRadius: '3px', border: '1px solid #ccc' }}
                  />
                </th>
                <th></th>
                <th>
                  <input
                    type="text"
                    placeholder="Distributor..."
                    value={columnFilters.distributor || ''}
                    onChange={(e) => onColumnFilterChange('distributor', e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ width: '110px', padding: '3px 6px', fontSize: '11.5px', borderRadius: '3px', border: '1px solid #ccc' }}
                  />
                </th>
                <th>
                  <div className="d-flex align-items-center gap-1">
                    <input
                      type="text"
                      placeholder="Search client..."
                      value={columnFilters.subscriber || ''}
                      onChange={(e) => onColumnFilterChange('subscriber', e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={{ width: '130px', padding: '3px 6px', fontSize: '11.5px', borderRadius: '3px', border: '1px solid #ccc' }}
                    />
                    <button type="button" className="btn btn-default btn-xs" onClick={onApplyFilters}>
                      <FontAwesomeIcon icon={faSearch} />
                    </button>
                  </div>
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Service..."
                    value={columnFilters.service || ''}
                    onChange={(e) => onColumnFilterChange('service', e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ width: '100px', padding: '3px 6px', fontSize: '11.5px', borderRadius: '3px', border: '1px solid #ccc' }}
                  />
                </th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, rIdx) => (
                  <tr key={`skel-row-${rIdx}`} className="skeleton-row">
                    {Array.from({ length: 8 }).map((_, cIdx) => (
                      <td key={`skel-cell-${cIdx}`}>
                        <div className="skeleton-text" style={{ width: '80%', height: '14px' }}></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length > 0 ? (
                data.map((row, idx) => (
                  <tr key={row.id || idx}>
                    <td>{(page - 1) * limit + idx + 1}</td>
                    <td>{row.orderId}</td>
                    <td>{row.createdOn}</td>
                    <td>
                      <div>{row.distributorName}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{row.referralCode}</div>
                    </td>
                    <td>
                      <div>{row.subscriberName}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        {row.subscriberMobile} {row.subscriberEmail && row.subscriberEmail !== '—' ? `| ${row.subscriberEmail}` : ''}
                      </div>
                    </td>
                    <td>{row.serviceName}</td>
                    <td>₹{Number(row.subscriptionAmount || 0).toLocaleString()}</td>
                    <td>
                      ₹{Number(row.commissionAmount || 0).toLocaleString()} ({row.commissionPercentage}%)
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center" style={{ padding: '30px', color: '#888' }}>
                    No commission transaction records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DistributorLedgerTable;
